/* -----------------Globals--------------- */
import {
	type AccessibilityRole,
	type LayoutChangeEvent,
	Platform,
	type Role,
	View,
	type ViewStyle,
} from "react-native";

export type AlignItems =
	| "stretch"
	| "center"
	| "flex-start"
	| "flex-end"
	| "baseline";
/* -----------------Props--------------- */
export type BoxProps = {
	children?: React.ReactNode;
	zIndex?: number;
	borderColor?: string;
	borderWidth?: number;
	borderStyle?: "solid" | "dotted" | "dashed";
	borderRadius?: number | string;
	minWidth?: number;
	maxWidth?: string | number;
	borderRoundness?: number;
	display?: "none" | "flex";
	height?: number | string;
	width?: ViewStyle["width"];
	position?: "sticky" | "relative" | "absolute" | "fixed";
	flexDirection?: "row" | "column" | "row-reverse" | "column-reverse";
	justifyContent?:
		| "center"
		| "flex-start"
		| "flex-end"
		| "space-between"
		| "space-around"
		| "space-evenly";
	maxHeight?: number | string;
	minHeight?: number | string;
	alignItems?: AlignItems;
	alignSelf?: "flex-start" | "flex-end" | "center" | "stretch" | "baseline";
	padding?: number;
	paddingVertical?: number;
	paddingHorizontal?: number;
	paddingTop?: number;
	paddingRight?: number;
	paddingBottom?: number;
	paddingLeft?: number;
	margin?: number;
	borderTopWidth?: number;
	borderTopColor?: string;
	borderBottomWidth?: number;
	borderBottomColor?: string;
	borderRightWidth?: number;
	borderRightColor?: string;
	borderLeftWidth?: number;
	borderLeftColor?: string;
	overflow?: string;
	marginVertical?: number;
	marginHorizontal?: number;
	marginTop?: number;
	marginRight?: number;
	marginBottom?: number;
	marginLeft?: number;
	color?: string;
	fontColor?: string;
	backgroundColor?: string;
	flex?: number;
	flexWrap?: "wrap" | "nowrap" | "wrap-reverse";
	flexGrow?: number;
	alignContent?: "center";
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	style?: any;
	gap?: string | number;
	elevation?: number;
	borderTopLeftRadius?: number;
	borderTopRightRadius?: number;
	pointerEvents?: "none" | "auto";
	onLayout?: (e: LayoutChangeEvent) => void;
	left?: number;
	top?: number | string;
	bottom?: string | number;
	right?: number;
	cursor?: "pointer" | "default" | "grab";
	columnGap?: number;
	rowGap?: number;
	onMouseEnter?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onMouseLeave?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onContextMenu?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	id?: string;
	accessibilityRole?: AccessibilityRole;
	accessibilityLabel?: string;
	role?: Role;
	userSelect?: "none" | "text";
};

/* -----------------Component--------------- */
const Box = (props: BoxProps) => {
	const {
		children,
		onLayout,
		style,
		onMouseEnter,
		onMouseLeave,
		onContextMenu,
		id,
		accessibilityRole,
		accessibilityLabel,
		role,
		display,
		...rest
	} = props;
	const handleOnLayout = (e: LayoutChangeEvent) => {
		if (onLayout) {
			onLayout(e);
		}
	};
	return (
		<View
			onLayout={handleOnLayout}
			accessibilityRole={accessibilityRole}
			accessibilityLabel={accessibilityLabel}
			role={role}
			style={[style, { ...rest, display: display || "flex" }]}
			{...(Platform.OS === "web"
				? { onMouseEnter, onMouseLeave, onContextMenu, id }
				: {})}
		>
			{children}
		</View>
	);
};

export default Box;
